import * as Sentry from "@sentry/react";
import type { ApiError } from "../../api/client/types/common.types";
import type { ApiErrorResponse } from "../../api/client/types";
import { toast } from "react-hot-toast";

/** Build full URL from axios request config (mirrors axiosClient.buildFullUrl). */
function buildFullUrlFromConfig(config: { baseURL?: string; url?: string; params?: Record<string, unknown> }): string {
  const cleanBaseURL = (config.baseURL ?? "").replace(/\/+$/, "");
  const cleanUrlPath = (config.url ?? "").replace(/^\/+/, "");
  const queryString = config.params
    ? "?" + new URLSearchParams(config.params as Record<string, string>).toString()
    : "";
  return `${cleanBaseURL}/${cleanUrlPath}${queryString}`;
}

/**
 * Safely extract the backend error message based on your API schema.
 */
export function extractApiError(error: unknown): string {
  const apiError = error as ApiError | undefined;
  const status = apiError?.response?.status;
  const responseData = apiError?.response?.data as ApiErrorResponse | undefined;

  // 1️⃣ Server errors → Hide backend message, always show generic
  if (status != null && status >= 500) {
    // Safety net: ensure 5xx is always reported (e.g. if request didn't go through axios interceptor)
    Sentry.withScope((scope) => {
      scope.setTag("layer", "error_handler");
      scope.setTag("http.status_code", String(status));

      const config = apiError?.response?.config;
      const method = config?.method?.toUpperCase() ?? "UNKNOWN";
      const fullUrl = config ? buildFullUrlFromConfig(config) : "unknown";

      scope.setTag("http.method", method);
      scope.setTag("http.url", fullUrl);
      scope.setTag("api_error_code", responseData?.error?.code ?? "unknown");

      scope.setContext("http_request", {
        url: fullUrl,
        method,
        full_url: fullUrl,
      });

      scope.setContext("api_error", {
        message: responseData?.error?.message,
        code: responseData?.error?.code,
        request_id: responseData?.error?.request_id,
        details: responseData?.error?.details,
      });

      if (responseData?.error?.message) {
        scope.setExtra("api_message", responseData.error.message);
      }

      Sentry.captureException(error);
    });
    return "El servidor no funcionó. Inténtalo más tarde.";
  }

  const message =
    responseData?.error?.message ??
    apiError?.message ??
    "Ocurrió un error inesperado. Intenta nuevamente.";

  return typeof message === "string" ? message : String(message);
}

/**
 * Whether a failed request is worth retrying (transient/server/network issues).
 * Retry only when: status >= 500 (server error) or no status in response (e.g. network error).
 * Do not retry on 4xx (client errors) — they are not transient.
 */
export function shouldRetryRequest(error: unknown): boolean {
  const apiError = error as ApiError | undefined;
  const status = apiError?.response?.status;
  if (status != null && status < 500) return false;
  return true;
}

/**
 * Shows a toast using the extracted message (or fallback).
 */
export function showApiErrorToast(error: unknown, fallback?: string) {
  const message = extractApiError(error);
  toast.error(message || fallback || "Error desconocido");
}
